import { useState, useRef, useEffect } from "react";
import { useProcurementUser, DEMO_USERS } from "../context/ProcurementUserContext";

export default function ProcurementUserSwitcher() {
  const { activeUser, setActiveUser } = useProcurementUser();
  const [open, setOpen] = useState(false);
  const [justSwitched, setJustSwitched] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function switchUser(user) {
    setActiveUser(user);
    setOpen(false);
    setJustSwitched(true);
    setTimeout(() => setJustSwitched(false), 2000);
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", zIndex: 1000 }}>
      <button onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: "0.6rem", background: justSwitched ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)", border: `1px solid ${justSwitched ? "#22c55e" : activeUser.color}`, borderRadius: 10, padding: "0.45rem 0.875rem", cursor: "pointer", transition: "all 0.2s", color: "#F4F0E8" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: justSwitched ? "#22c55e" : activeUser.color, flexShrink: 0, boxShadow: `0 0 6px ${activeUser.color}` }} />
        <span style={{ fontFamily: "monospace", fontSize: 11, letterSpacing: "0.04em", color: justSwitched ? "#22c55e" : activeUser.color, fontWeight: 600 }}>{justSwitched ? "\u2713 Switched" : activeUser.badge}</span>
        <span style={{ fontSize: "0.82rem", color: "#F4F0E8", fontWeight: 500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeUser.label}</span>
        <span style={{ fontSize: 10, color: "#5C657A", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>{"\u25BC"}</span>
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 300, background: "#161C2E", border: "1px solid #2A3048", borderRadius: 14, padding: "0.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", animation: "fadeInSwitcher 0.12s ease" }}>
          <div style={{ padding: "0.4rem 0.75rem 0.6rem", borderBottom: "1px solid #2A3048", marginBottom: "0.4rem" }}>
            <span style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C657A" }}>Demo — Switch User View</span>
          </div>
          <div style={{ padding: "0.25rem 0.75rem 0.2rem" }}>
            <span style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#C9972B" }}>Internal — ADC Team</span>
          </div>
          {DEMO_USERS.filter(u => u.badge === "INTERNAL").map(user => (
            <UserOption key={user.identifier} user={user} active={activeUser.identifier === user.identifier} onClick={() => switchUser(user)} />
          ))}
          <div style={{ height: 1, background: "#2A3048", margin: "0.5rem 0" }} />
          <div style={{ padding: "0.25rem 0.75rem 0.2rem" }}>
            <span style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#34d399" }}>External — Vendor Access</span>
          </div>
          {DEMO_USERS.filter(u => u.badge === "VENDOR" || u.badge === "EXTERNAL").map(user => (
            <UserOption key={user.identifier} user={user} active={activeUser.identifier === user.identifier} onClick={() => switchUser(user)} />
          ))}
          <div style={{ margin: "0.5rem 0 0.25rem", padding: "0.5rem 0.75rem", background: "rgba(201,151,43,0.06)", border: "1px solid rgba(201,151,43,0.15)", borderRadius: 8 }}>
            <span style={{ fontSize: 10, color: "#7A5A18", lineHeight: 1.5 }}>Demo only — switching user reloads all page data with the selected user's access scope.</span>
          </div>
        </div>
      )}
      <style>{`@keyframes fadeInSwitcher { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

function UserOption({ user, active, onClick }) {
  const [hover, setHover] = useState(false);
  const rgb = hexToRgb(user.color);
  return (
    <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: "0.6rem", padding: "0.55rem 0.75rem", borderRadius: 8, border: "none", cursor: "pointer", textAlign: "left", background: active ? `rgba(${rgb}, 0.1)` : hover ? "rgba(255,255,255,0.04)" : "transparent", transition: "background 0.15s" }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: user.color, flexShrink: 0, marginTop: 4, boxShadow: active ? `0 0 8px ${user.color}` : "none" }} />
      <div>
        <div style={{ fontSize: "0.83rem", fontWeight: 600, color: active ? user.color : "#F4F0E8", marginBottom: 2 }}>
          {user.label}
          {active && <span style={{ marginLeft: 6, fontFamily: "monospace", fontSize: 9, color: user.color, letterSpacing: "0.1em" }}>ACTIVE</span>}
        </div>
        <div style={{ fontSize: "0.74rem", color: "#5C657A", lineHeight: 1.4 }}>{user.description}</div>
      </div>
    </button>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3), 16);
  const g = parseInt(hex.slice(3,5), 16);
  const b = parseInt(hex.slice(5,7), 16);
  return `${r},${g},${b}`;
}
